import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { Search, SquareCheck, ThumbsUp, Star, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { useProviderUser } from "@/hooks/useProviderUser";

interface TabButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  to: string;
  tabRef?: React.RefObject<HTMLAnchorElement | null>;
}

// --- Application Components ---

const TabButton: React.FC<TabButtonProps> = ({
  icon,
  label,
  active,
  to,
  tabRef,
}) => (
  <Link
    ref={tabRef}
    to={to}
    aria-current={active ? "page" : undefined}
    className={`relative flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl px-4 text-sm font-medium whitespace-nowrap transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
                ${
                  active
                    ? "text-primary bg-primary/5 font-semibold after:absolute after:inset-x-0 after:-bottom-3 after:h-0.5 after:rounded-full after:bg-primary"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
  >
    {icon}
    <span
      className={`transition-all duration-300 ${active ? "font-semibold" : ""}`}
    >
      {label}
    </span>
  </Link>
);

// Main App Component
export function ProviderJobLayout({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const location = useLocation();
  const trpc = useTRPC();
  // Roster-only providers (marketplace job posts disabled) never see the
  // browse/find/interests tabs — only their assigned work and timesheets.
  const { jobPostsEnabled } = useProviderUser();
  const activeTabRef = useRef<HTMLAnchorElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // State for header visibility
  const [isHeaderVisible, setIsHeaderVisible] = useState<boolean>(true);
  const [lastScrollY, setLastScrollY] = useState<number>(0);

  // Check if user has any job offers (open or accepted)
  const { data: acceptedJobs } = useQuery(
    trpc.jobOffer.providerListAccepted.queryOptions(),
  );
  const { data: openJobOffers } = useQuery(
    trpc.jobOffer.providerListOpen.queryOptions(),
  );
  const { data: timesheetData } = useQuery(
    trpc.timeEntry.getShiftsNeedingEntries.queryOptions(),
  );

  // Providers hired at a business unit can add hours without any job offer.
  const { data: adHocBusinessUnits } = useQuery(
    trpc.timeEntry.getAdHocEntryBusinessUnits.queryOptions(),
  );

  const hasAnyJobOffers =
    (acceptedJobs?.length ?? 0) > 0 || (openJobOffers?.length ?? 0) > 0;
  const hasTimesheetsNeeded =
    timesheetData?.some((group) => group.totalShifts > 0) ?? false;
  const canAddAdHocHours = (adHocBusinessUnits?.length ?? 0) > 0;

  // Define tab configuration with route matching
  const allTabs = useMemo(
    () => [
      {
        key: "Active Jobs",
        icon: <SquareCheck className="h-5 w-5" />,
        label: jobPostsEnabled ? "My Jobs" : "My Work",
        to: "/provider/jobs/active",
        isActive: location.pathname === "/provider/jobs/active",
        requiresJobOffers: true,
      },
      {
        key: "Timesheets",
        icon: <Clock className="h-5 w-5" />,
        label: "Timesheets",
        to: "/provider/jobs/timesheets",
        isActive: location.pathname === "/provider/jobs/timesheets",
        isVisible: hasAnyJobOffers || hasTimesheetsNeeded || canAddAdHocHours,
      },
      {
        key: "Recommended",
        icon: <Star className="h-5 w-5" />,
        label: "Recommended",
        to: "/provider/jobs/recommended",
        isActive: location.pathname === "/provider/jobs/recommended",
        marketplaceOnly: true,
      },
      {
        key: "Find Jobs",
        icon: <Search className="h-5 w-5" />,
        label: "Find Jobs",
        to: "/provider/jobs/search",
        isActive: location.pathname === "/provider/jobs/search",
        marketplaceOnly: true,
      },
      {
        key: "Interests",
        icon: <ThumbsUp className="h-5 w-5" />,
        label: "Interests",
        to: "/provider/jobs/interests",
        isActive: location.pathname === "/provider/jobs/interests",
        marketplaceOnly: true,
      },
    ],
    [
      hasAnyJobOffers,
      hasTimesheetsNeeded,
      canAddAdHocHours,
      jobPostsEnabled,
      location.pathname,
    ],
  );

  // Filter tabs based on marketplace access and whether user has job offers
  const tabs = useMemo(() => {
    return allTabs.filter((tab) => {
      if ("marketplaceOnly" in tab && tab.marketplaceOnly && !jobPostsEnabled) {
        return false;
      }
      if ("isVisible" in tab) {
        return tab.isVisible;
      }
      return !tab.requiresJobOffers || hasAnyJobOffers;
    });
  }, [allTabs, hasAnyJobOffers, jobPostsEnabled]);

  // Scroll detection for header auto-hide
  useEffect(() => {
    const controlHeader = () => {
      if (!contentRef.current) {
        return;
      }

      const currentScrollY = contentRef.current.scrollTop;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down and past 100px
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY || currentScrollY <= 100) {
        // Scrolling up or near top
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener("scroll", controlHeader);
      return () => contentElement.removeEventListener("scroll", controlHeader);
    }
  }, []);

  // Keep the selected tab visible as routes and available tabs change.
  useEffect(() => {
    if (activeTabRef.current && scrollContainerRef.current) {
      const activeTab = activeTabRef.current;
      const container = scrollContainerRef.current;

      // Get the positions
      const containerRect = container.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();

      // Check if tab is outside the visible area
      const isTabOutOfView =
        tabRect.left < containerRect.left ||
        tabRect.right > containerRect.right;

      if (isTabOutOfView) {
        // Calculate scroll position to center the active tab
        const scrollLeft =
          activeTab.offsetLeft -
          container.offsetWidth / 2 +
          activeTab.offsetWidth / 2;

        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        });
      }
    }
  }, [location.pathname, tabs]);

  return (
    <div className="grid min-w-0 flex-1 grid-rows-[auto_1fr] bg-slate-50">
      {/* Job Header with Auto-hide */}
      <div
        className={`sticky top-0 z-40 overflow-hidden border-b border-slate-200 bg-white transition-all duration-300 ease-in-out ${
          isHeaderVisible
            ? "translate-y-0 opacity-100 max-h-20"
            : "-translate-y-full opacity-0 max-h-0"
        }`}
      >
        {/* Scrollable tab container with scroll indicators */}
        <div className="relative w-full max-w-[100dvw]">
          {/* Left gradient overlay */}
          <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none md:hidden"></div>

          {/* Right gradient overlay */}
          <div className="absolute right-0 top-0 bottom-0 w-3 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none md:hidden"></div>

          {/* Scrollable container */}
          <div
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-hide relative"
          >
            <nav
              aria-label="Provider jobs"
              className="flex min-w-max items-center justify-start gap-1 px-4 py-3 md:justify-center md:px-8"
            >
              {tabs.map((tab) => (
                <TabButton
                  key={tab.key}
                  icon={React.cloneElement(tab.icon, {
                    className: `h-4 w-4 transition-colors ${tab.isActive ? "text-primary" : "text-slate-500"}`,
                  })}
                  label={tab.label}
                  active={tab.isActive}
                  to={tab.to}
                  tabRef={tab.isActive ? activeTabRef : undefined}
                />
              ))}
            </nav>
          </div>
        </div>
      </div>

      <div
        ref={contentRef}
        className="w-full min-w-0 overflow-y-auto px-4 py-6 sm:p-6"
      >
        <div className="mx-auto w-full max-w-7xl">{children}</div>
      </div>
    </div>
  );
}
